import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { CountryStats, Operator } from '../types';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const CountryGrid: React.FC = () => {
  const [countryData, setCountryData] = useState<CountryStats[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountryStats | null>(null);
  const [countryOperators, setCountryOperators] = useState<Operator[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(1);
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    fetchCountryData();
  }, [period]);

  const fetchCountryData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStatsByCountry({ days: period });
      setCountryData(data);
    } catch (error) {
      console.error('Error fetching country data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryClick = async (country: CountryStats) => {
    setSelectedCountry(country);
    setShowPanel(true);
    setLoadingOperators(true);
    try {
      const response = await apiService.getOperators({
        country: country.country,
        size: 100,
      });
      setCountryOperators(response.items || []);
    } catch (error) {
      console.error('Error fetching country operators:', error);
      setCountryOperators([]);
    } finally {
      setLoadingOperators(false);
    }
  };

  const maxCount = Math.max(...countryData.map(c => c.registration_count), 1);

  const getCountryColor = (count: number) => {
    const intensity = count / maxCount;
    if (intensity > 0.7) return 'bg-blue-900 text-white';
    if (intensity > 0.4) return 'bg-blue-700 text-white';
    if (intensity > 0.2) return 'bg-blue-500 text-white';
    return 'bg-blue-300 text-blue-900';
  };

  const countryFlags: { [key: string]: string } = {
    ES: '🇪🇸', FR: '🇫🇷', DE: '🇩🇪', IT: '🇮🇹', GB: '🇬🇧',
    LU: '🇱🇺', CO: '🇨🇴', BY: '🇧🇾', NO: '🇳🇴', DJ: '🇩🇯',
    CV: '🇨🇻', IS: '🇮🇸', MC: '🇲🇨', CW: '🇨🇼', BE: '🇧🇪',
    NL: '🇳🇱', AT: '🇦🇹', CH: '🇨🇭', PT: '🇵🇹', SE: '🇸🇪',
    DK: '🇩🇰', FI: '🇫🇮', PL: '🇵🇱', RO: '🇷🇴', GR: '🇬🇷',
    CZ: '🇨🇿', HU: '🇭🇺', BG: '🇧🇬', HR: '🇭🇷', SI: '🇸🇮',
    SK: '🇸🇰', EE: '🇪🇪', LV: '🇱🇻', LT: '🇱🇹', MT: '🇲🇹',
    CY: '🇨🇾', IE: '🇮🇪'
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">World Map - Country View</h1>
        <p className="mt-2 text-sm text-gray-700">
          Countries colored by roaming activity. Click to see operator details.
        </p>
      </div>

      {/* Country Details Panel */}
      {showPanel && selectedCountry && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50" style={{ zIndex: 9998 }} onClick={() => setShowPanel(false)} />
          <div className="fixed top-20 left-4 right-4 bg-white shadow-2xl rounded-lg p-6 max-w-3xl mx-auto" style={{ zIndex: 9999 }}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-4xl">{countryFlags[selectedCountry.country_iso2] || '🌍'}</span>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedCountry.country}
                  </h2>
                  <p className="text-sm text-gray-500">ISO: {selectedCountry.country_iso2}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedCountry(null);
                  setShowPanel(false);
                }}
                className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Total Registrations</p>
              <p className="text-3xl font-bold text-blue-900">
                {selectedCountry.registration_count.toLocaleString()}
              </p>

              <div className="mt-4 grid grid-cols-3 gap-3 border-t border-blue-100 pt-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Allowed</p>
                  <p className="text-xl font-semibold text-green-700 tabular-nums">
                    {selectedCountry.allowed_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Denied</p>
                  <p className="text-xl font-semibold text-red-700 tabular-nums">
                    {selectedCountry.denied_count.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Subscribers</p>
                  <p className="text-xl font-semibold text-gray-900 tabular-nums">
                    {selectedCountry.subscriber_count.toLocaleString()}
                  </p>
                  <p className="text-[11px] text-gray-500">distinct IMSIs</p>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-blue-100 pt-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">HLR queries</p>
                  <p className="text-xl font-semibold text-gray-900 tabular-nums">
                    {selectedCountry.hlr_count.toLocaleString()}
                  </p>
                  <p className="text-[11px] text-gray-500">2G / 3G</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">HSS queries</p>
                  <p className="text-xl font-semibold text-gray-900 tabular-nums">
                    {selectedCountry.hss_count.toLocaleString()}
                  </p>
                  <p className="text-[11px] text-gray-500">4G / 5G</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 sticky top-0 bg-white pb-2">
                Network Operators
              </h3>
              {loadingOperators ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading operators...</p>
                </div>
              ) : countryOperators.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No operators found</p>
              ) : (
                <div className="space-y-3">
                  {countryOperators.map((operator) => (
                    <div
                      key={operator.id}
                      className={`p-4 rounded-lg border-2 ${
                        operator.is_allowed 
                          ? 'bg-green-50 border-green-300 hover:border-green-400' 
                          : 'bg-red-50 border-red-300 hover:border-red-400'
                      } transition-all`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            {operator.is_allowed ? (
                              <CheckIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                            ) : (
                              <XMarkIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                            )}
                            <div>
                              <div className="flex items-baseline space-x-2">
                                <span className="font-bold text-lg text-gray-900">
                                  {operator.tadig_code}
                                </span>
                                <span className="text-gray-600">
                                  • {operator.operator_name}
                                </span>
                              </div>
                              <div className="mt-1 space-x-3 text-sm text-gray-500">
                                <span>MCC+MNC: {operator.mcc_mnc}</span>
                                {operator.roaming_agreement_type && (
                                  <span>• Type: {operator.roaming_agreement_type.replace(/_/g, ' ')}</span>
                                )}
                                {operator.gt_prefix_count > 0 && (
                                  <span>• GT Prefixes: {operator.gt_prefix_count}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          operator.is_allowed
                            ? 'bg-green-200 text-green-800'
                            : 'bg-red-200 text-red-800'
                        }`}>
                          {operator.is_allowed ? 'ALLOWED' : 'DENIED'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{countryOperators.length}</p>
                  <p className="text-sm text-gray-500">Total Operators</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {countryOperators.filter(op => op.is_allowed).length}
                  </p>
                  <p className="text-sm text-gray-500">Allowed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {countryOperators.filter(op => !op.is_allowed).length}
                  </p>
                  <p className="text-sm text-gray-500">Denied</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Period Selector */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Period:</span>
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-600">
            Total countries: <span className="font-medium">{countryData.length}</span>
          </div>
        </div>
      </div>

      {/* Country Grid */}
      <div className="bg-white shadow rounded-lg p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading country data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Color Legend */}
            <div className="mb-6 flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-900 rounded mr-2"></div>
                <span>High Activity ({Math.floor(maxCount * 0.7)}+)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-700 rounded mr-2"></div>
                <span>Medium ({Math.floor(maxCount * 0.4)}-{Math.floor(maxCount * 0.7)})</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2"></div>
                <span>Low ({Math.floor(maxCount * 0.2)}-{Math.floor(maxCount * 0.4)})</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-300 rounded mr-2"></div>
                <span>Minimal (1-{Math.floor(maxCount * 0.2)})</span>
              </div>
            </div>

            {/* Countries Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {countryData
                .sort((a, b) => b.registration_count - a.registration_count)
                .map((country) => (
                <div
                  key={country.country_iso2}
                  onClick={() => handleCountryClick(country)}
                  className={`relative p-6 rounded-lg cursor-pointer transition-all transform hover:scale-105 hover:shadow-xl ${getCountryColor(country.registration_count)}`}
                >
                  <div className="text-center">
                    <div className="text-4xl mb-2">
                      {countryFlags[country.country_iso2] || '🌍'}
                    </div>
                    <h3 className="font-bold text-lg">
                      {country.country}
                    </h3>
                    <p className="text-sm opacity-90">
                      {country.country_iso2}
                    </p>
                    <div className="mt-3 text-3xl font-bold">
                      {country.registration_count}
                    </div>
                    <p className="text-xs opacity-75">registrations</p>
                    <div className="mt-2 flex items-center justify-center gap-2 text-xs font-medium">
                      <span title="Allowed">&#10003; {country.allowed_count}</span>
                      <span className="opacity-50">|</span>
                      <span title="Denied">&#10007; {country.denied_count}</span>
                    </div>
                    <p className="mt-1 text-xs opacity-75">
                      HLR {country.hlr_count} &middot; HSS {country.hss_count}
                    </p>
                    <p className="text-xs opacity-75">
                      {country.subscriber_count} {country.subscriber_count === 1 ? 'subscriber' : 'subscribers'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CountryGrid;